import React, { useMemo } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { ThemeProvider } from 'styled-components'
import { ThemeProvider as MUIThemeProvider, CssBaseline, createTheme } from '@mui/material'
import { heIL, esES, arSA, thTH, enUS } from '@mui/material/locale'
import { heIL as pickersHeIL, esES as pickersEsES } from '@mui/x-date-pickers/locales'
import { CacheProvider } from '@emotion/react'
import createCache from '@emotion/cache'
import { prefixer } from 'stylis'
import rtlPlugin from 'stylis-plugin-rtl'
import { AuthProvider } from './contexts/AuthContext'
import { SocketProvider } from './contexts/SocketContext'
import { SettingsProvider, useSettings } from './contexts/SettingsContext'
import { theme } from './styles/theme'
import muiTheme from './styles/muiTheme'
import LoginScreen from './screens/LoginScreen'
import ForgotPasswordScreen from './screens/ForgotPasswordScreen'
import ResetPasswordScreen from './screens/ResetPasswordScreen'
import AcceptInvitationScreen from './screens/AcceptInvitationScreen'
import RegisterScreen from './screens/RegisterScreen'
import DashboardScreen from './screens/DashboardScreen'
import YardsScreen from './screens/YardsScreen'
import YardDetailsScreen from './screens/YardDetailsScreen'
import InventoryScreen from './screens/InventoryScreen'
import TeamsScreen from './screens/TeamsScreen'
import UserManagementScreen from './screens/UserManagementScreen'
import UserDetailsScreen from './screens/UserDetailsScreen'
import AnalysisScreen from './screens/AnalysisScreen'
import TaskManagementScreen from './screens/TaskManagementScreen'
import RecommendationsScreen from './screens/RecommendationsScreen'
import SettingsScreen from './screens/SettingsScreen'
import AccountsScreen from './screens/AccountsScreen'
import DebugScreen from './screens/DebugScreen'
import ElapScreen from './screens/ElapScreen'
import ReportsScreen from './screens/ReportsScreen'
import DevicesScreen from './screens/DevicesScreen'
import DeviceDetailsScreen from './screens/DeviceDetailsScreen'
import LandingScreen from './screens/LandingScreen'
import ProtectedRoute from './components/common/ProtectedRoute'
import Layout from './components/common/Layout'
import './translations/i18n'

/**
 * Emotion caches for MUI. The RTL cache runs stylis-plugin-rtl so MUI's own
 * styles (margins, paddings, left/right, border radii) are mirrored in he/ar —
 * `direction: 'rtl'` on the theme alone does not flip them.
 * styled-components styles are not affected by these caches.
 */
const cacheRtl = createCache({ key: 'muirtl', stylisPlugins: [prefixer, rtlPlugin] })
const cacheLtr = createCache({ key: 'mui' })

/**
 * MUI built-in texts (pagination "Rows per page", Autocomplete "No options", …)
 * per UI language. Date-picker texts exist for he/es only; ar/th fall back to English.
 */
const MUI_LOCALES: Record<string, object[]> = {
  en: [enUS],
  he: [heIL, pickersHeIL],
  es: [esES, pickersEsES],
  ar: [arSA],
  th: [thTH],
}

/**
 * Inner app with settings-aware MUI theme direction and locale
 */
function AppContent(): React.JSX.Element {
  const { isRTL, language } = useSettings()

  // Dynamically create MUI theme with RTL direction and the MUI locale for the UI language
  const dynamicMuiTheme = useMemo(
    () => createTheme({ ...muiTheme, direction: isRTL ? 'rtl' : 'ltr' }, ...(MUI_LOCALES[language] || MUI_LOCALES.en)),
    [isRTL, language]
  )

  return (
    <CacheProvider value={isRTL ? cacheRtl : cacheLtr}>
      <MUIThemeProvider theme={dynamicMuiTheme}>
        <CssBaseline />
        <ThemeProvider theme={theme}>
          <Router>
            <Routes>
              <Route path="/" element={<LandingScreen />} />
              <Route path="/login" element={<LoginScreen />} />
              <Route path="/forgot-password" element={<ForgotPasswordScreen />} />
              <Route path="/reset-password" element={<ResetPasswordScreen />} />
              <Route path="/accept-invitation" element={<AcceptInvitationScreen />} />
              <Route path="/register" element={<RegisterScreen />} />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <DashboardScreen />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/yards"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <YardsScreen />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/yards/:yardId"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <YardDetailsScreen />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/inventory"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <InventoryScreen />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/devices"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <DevicesScreen />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/devices/:macAddress"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <DeviceDetailsScreen />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/teams"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <TeamsScreen />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/analysis"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <AnalysisScreen />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tasks"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <TaskManagementScreen />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/recommendations"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <RecommendationsScreen />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <SettingsScreen />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/users"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <UserManagementScreen />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/accounts"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <AccountsScreen />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/elap"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <ElapScreen />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/reports"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <ReportsScreen />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/debug"
                element={
                  <ProtectedRoute requiredRole="super_admin">
                    <Layout>
                      <DebugScreen />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/user-management"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <UserManagementScreen />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/user-management/:userId"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <UserDetailsScreen />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route path="*" element={<Navigate to="/dashboard" />} />
            </Routes>
          </Router>
        </ThemeProvider>
      </MUIThemeProvider>
    </CacheProvider>
  )
}

/**
 * Main App component
 * @returns App component
 */
function App(): React.JSX.Element {
  return (
    <AuthProvider>
      <SocketProvider>
        <SettingsProvider>
          <AppContent />
        </SettingsProvider>
      </SocketProvider>
    </AuthProvider>
  )
}

export default App
